<template>
  <div class="home">
    <Navbar :is-active="6"></Navbar>
    <div class="body">
      <MesasSidebar active="comprobantes" />
      <div class="body__containt">
        <div class="page__title">
          <open-arrow></open-arrow>
          <div>
            <h2>Comprobantes</h2>
            <p class="page__lead">
              Boletas y facturas emitidas al cobrar mesas (Nubefact o simuladas).
            </p>
          </div>
        </div>

        <div class="page__filters dash-filters">
          <label class="dash-field">
            <span>Desde</span>
            <input type="date" v-model="desde" />
          </label>
          <label class="dash-field">
            <span>Hasta</span>
            <input type="date" v-model="hasta" />
          </label>
          <label class="dash-field">
            <span>Tipo</span>
            <select v-model="tipo">
              <option value="all">Todos</option>
              <option value="boleta">Boletas</option>
              <option value="factura">Facturas</option>
            </select>
          </label>
          <label class="dash-field">
            <span>Origen</span>
            <select v-model="provider">
              <option value="all">Todos</option>
              <option value="nubefact">Nubefact</option>
              <option value="mock">Simulado</option>
            </select>
          </label>
          <label class="dash-field dash-field--grow">
            <span>Buscar</span>
            <input
              type="search"
              v-model="q"
              placeholder="Serie, cliente, mesa, ID…"
              @keyup.enter="load(1)"
            />
          </label>
          <button class="btn-search" :disabled="busy" @click="load(1)">
            {{ busy ? 'Cargando…' : 'Buscar' }}
          </button>
        </div>

        <section class="kpi-grid" v-if="summary">
          <article class="kpi-card kpi-card--accent">
            <p class="kpi-card__label">Total</p>
            <h3 class="kpi-card__value">{{ summary.total }}</h3>
            <p class="kpi-card__meta">S/ {{ money(summary.amount_total) }}</p>
          </article>
          <article class="kpi-card">
            <p class="kpi-card__label">Boletas</p>
            <h3 class="kpi-card__value">{{ summary.boletas }}</h3>
            <p class="kpi-card__meta">tipo 2</p>
          </article>
          <article class="kpi-card">
            <p class="kpi-card__label">Facturas</p>
            <h3 class="kpi-card__value">{{ summary.facturas }}</h3>
            <p class="kpi-card__meta">tipo 1</p>
          </article>
          <article class="kpi-card">
            <p class="kpi-card__label">Nubefact</p>
            <h3 class="kpi-card__value">{{ summary.nubefact }}</h3>
            <p class="kpi-card__meta">
              {{ summary.mock }} simulados
              <template v-if="summary.failed"> · {{ summary.failed }} con error</template>
            </p>
          </article>
        </section>

        <p v-if="error" class="dash-error">{{ error }}</p>

        <div class="table-wrap" v-if="items.length">
          <table class="cpe-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Comprobante</th>
                <th>Tipo</th>
                <th>Mesa</th>
                <th>Cliente</th>
                <th>Total</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in items" :key="row.invoice_id">
                <td>
                  <span class="mono">{{ formatDate(row.paid_at) }}</span>
                </td>
                <td>
                  <strong>{{ row.document_label || '—' }}</strong>
                  <span class="sub">#{{ row.invoice_id }}</span>
                </td>
                <td>
                  <span class="badge" :class="row.tipo === 'factura' ? 'badge--factura' : 'badge--boleta'">
                    {{ row.tipo === 'factura' ? 'Factura' : 'Boleta' }}
                  </span>
                </td>
                <td>
                  <template v-if="row.table_number != null">
                    #{{ row.table_number }} {{ row.table_name || '' }}
                  </template>
                  <template v-else>—</template>
                </td>
                <td>
                  <div>{{ row.guest_name || 'Invitado' }}</div>
                  <span class="sub" v-if="row.guest_doc_number">
                    {{ row.guest_doc_type || 'Doc' }} {{ row.guest_doc_number }}
                  </span>
                </td>
                <td class="num">S/ {{ money(row.total) }}</td>
                <td>
                  <span
                    class="badge"
                    :class="statusClass(row)"
                  >
                    {{ statusLabel(row) }}
                  </span>
                </td>
                <td class="actions">
                  <button type="button" class="link link--btn" @click="openStatus(row)">
                    Estado
                  </button>
                  <a
                    v-if="row.cpe_enlace"
                    class="link"
                    :href="row.cpe_enlace"
                    target="_blank"
                    rel="noopener"
                  >Nubefact</a>
                  <a
                    v-if="row.cpe_pdf || row.pdf_url"
                    class="link"
                    :href="row.cpe_pdf || row.pdf_url"
                    target="_blank"
                    rel="noopener"
                  >PDF</a>
                  <a
                    v-if="row.receipt_url"
                    class="link"
                    :href="row.receipt_url"
                    target="_blank"
                    rel="noopener"
                  >Recibo</a>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <p v-else-if="!busy && !error" class="dash-empty">
          No hay comprobantes en este rango. Cobra una mesa con Nubefact activo para verlos aquí.
        </p>

        <div class="pager" v-if="pagination && pagination.last_page > 1">
          <button class="btn-ghost" :disabled="page <= 1 || busy" @click="load(page - 1)">Anterior</button>
          <span>Pág. {{ pagination.page }} / {{ pagination.last_page }}</span>
          <button
            class="btn-ghost"
            :disabled="page >= pagination.last_page || busy"
            @click="load(page + 1)"
          >Siguiente</button>
        </div>

        <div class="modal" v-if="statusOpen" @click.self="closeStatus">
          <div class="modal__card" role="dialog" aria-modal="true" aria-labelledby="cpe-status-title">
            <header class="modal__head">
              <div>
                <p class="modal__eyebrow">Estado del comprobante</p>
                <h3 id="cpe-status-title">
                  {{ detail ? (detail.document_label || ('#' + detail.invoice_id)) : 'Cargando…' }}
                </h3>
              </div>
              <button type="button" class="modal__close" @click="closeStatus" aria-label="Cerrar">×</button>
            </header>

            <div class="modal__body" v-if="detailLoading">
              <p class="dash-empty">Cargando detalle…</p>
            </div>
            <div class="modal__body" v-else-if="detailError">
              <p class="dash-error">{{ detailError }}</p>
            </div>
            <div class="modal__body" v-else-if="detail">
              <div class="status-banner" :class="'status-banner--' + detail.status_key">
                <strong>{{ detail.status_label }}</strong>
                <span v-if="detail.message">{{ detail.message }}</span>
              </div>

              <div class="detail-grid">
                <div class="detail-item">
                  <span>Tipo</span>
                  <strong>{{ detail.tipo === 'factura' ? 'Factura' : 'Boleta' }}</strong>
                </div>
                <div class="detail-item">
                  <span>Serie / Número</span>
                  <strong>{{ detail.document_label || '—' }}</strong>
                </div>
                <div class="detail-item">
                  <span>Proveedor</span>
                  <strong>{{ detail.provider }} · {{ detail.mode }}</strong>
                </div>
                <div class="detail-item">
                  <span>Pagado</span>
                  <strong>{{ formatDate(detail.paid_at) }}</strong>
                </div>
                <div class="detail-item">
                  <span>Mesa</span>
                  <strong>
                    <template v-if="detail.table_number != null">
                      #{{ detail.table_number }} {{ detail.table_name || '' }}
                    </template>
                    <template v-else>—</template>
                  </strong>
                </div>
                <div class="detail-item">
                  <span>Total</span>
                  <strong>S/ {{ money(detail.total) }}</strong>
                </div>
                <div class="detail-item">
                  <span>Cliente</span>
                  <strong>{{ detail.guest_name || 'Invitado' }}</strong>
                </div>
                <div class="detail-item">
                  <span>Documento</span>
                  <strong>
                    <template v-if="detail.guest_doc_number">
                      {{ detail.guest_doc_type || 'Doc' }} {{ detail.guest_doc_number }}
                    </template>
                    <template v-else>—</template>
                  </strong>
                </div>
                <div class="detail-item">
                  <span>SUNAT</span>
                  <strong>
                    <template v-if="detail.aceptada_por_sunat === true">Aceptada</template>
                    <template v-else-if="detail.aceptada_por_sunat === false">Rechazada / pendiente</template>
                    <template v-else>—</template>
                  </strong>
                </div>
                <div class="detail-item">
                  <span>HTTP Nubefact</span>
                  <strong>{{ detail.http_status != null ? detail.http_status : '—' }}</strong>
                </div>
              </div>

              <div class="detail-block detail-block--warn" v-if="detail.errors || detail.sunat_description || detail.skip_reason">
                <h4>Detalle del error / SUNAT</h4>
                <p v-if="detail.skip_reason"><strong>Motivo:</strong> {{ detail.skip_reason }}</p>
                <p v-if="detail.sunat_description"><strong>SUNAT:</strong> {{ detail.sunat_description }}</p>
                <pre v-if="detail.errors">{{ formatErrors(detail.errors) }}</pre>
              </div>

              <div class="share-panel">
                <h4>Ver y compartir</h4>
                <div class="detail-links">
                  <a
                    v-if="detail.cpe_enlace"
                    class="btn-link"
                    :href="detail.cpe_enlace"
                    target="_blank"
                    rel="noopener"
                  >Ver ticket</a>
                  <a
                    v-if="detail.cpe_pdf"
                    class="btn-link"
                    :href="detail.cpe_pdf"
                    target="_blank"
                    rel="noopener"
                  >PDF Nubefact</a>
                  <a
                    v-else-if="detail.pdf_url"
                    class="btn-link"
                    :href="detail.pdf_url"
                    target="_blank"
                    rel="noopener"
                  >PDF</a>
                  <a
                    v-if="detail.receipt_url"
                    class="btn-link btn-link--ghost"
                    :href="detail.receipt_url"
                    target="_blank"
                    rel="noopener"
                  >Recibo local</a>
                  <a
                    v-if="detail.cpe_xml"
                    class="btn-link btn-link--ghost"
                    :href="detail.cpe_xml"
                    target="_blank"
                    rel="noopener"
                  >XML</a>
                </div>
                <p class="share-hint" v-if="!shareUrl">
                  Sin enlace de Nubefact (la emisión falló o fue simulada). Puedes compartir el recibo local si existe.
                </p>

                <div class="share-row">
                  <label class="share-field">
                    <span>Correo</span>
                    <input type="email" v-model="shareEmail" placeholder="cliente@correo.com" />
                  </label>
                  <button type="button" class="btn-link" :disabled="!canSendEmail" @click="sendEmail">
                    Enviar por correo
                  </button>
                </div>
                <div class="share-row">
                  <label class="share-field">
                    <span>WhatsApp</span>
                    <input type="tel" v-model="sharePhone" placeholder="999888777" />
                  </label>
                  <button type="button" class="btn-link btn-link--wa" :disabled="!canSendWhatsapp" @click="sendWhatsapp">
                    Enviar por WhatsApp
                  </button>
                </div>
              </div>

              <details class="raw-box" v-if="detail.raw" open>
                <summary>Respuesta cruda de Nubefact</summary>
                <pre>{{ prettyJson(detail.raw) }}</pre>
              </details>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Navbar from '@/components/NavBar.vue'
import MesasSidebar from '@/components/MesasSidebar.vue'
import OpenArrow from '@/components/OpenArrow.vue'

function todayISO() {
  return new Date().toISOString().slice(0, 10)
}

function daysAgoISO(n) {
  const d = new Date()
  d.setDate(d.getDate() - n)
  return d.toISOString().slice(0, 10)
}

export default {
  name: 'PosComprobantes',
  components: { Navbar, MesasSidebar, OpenArrow },
  data() {
    return {
      desde: daysAgoISO(29),
      hasta: todayISO(),
      tipo: 'all',
      provider: 'all',
      q: '',
      page: 1,
      busy: false,
      error: '',
      summary: null,
      items: [],
      pagination: null,
      statusOpen: false,
      detailLoading: false,
      detailError: '',
      detail: null,
      shareEmail: '',
      sharePhone: ''
    }
  },
  computed: {
    companyId() {
      return parseInt(localStorage.getItem('company_id'), 10)
    },
    shareUrl() {
      if (!this.detail) return ''
      return (
        this.detail.share_url ||
        this.detail.cpe_enlace ||
        this.detail.cpe_pdf ||
        this.detail.pdf_url ||
        this.detail.receipt_url ||
        ''
      )
    },
    canSendEmail() {
      return !!(this.shareEmail && this.shareUrl)
    },
    canSendWhatsapp() {
      return !!(this.normalizePhone(this.sharePhone) && this.shareUrl)
    }
  },
  created() {
    if (!localStorage.getItem('token')) {
      this.$router.push('/login')
      return
    }
    this.load(1)
  },
  methods: {
    money(v) {
      return (Math.round((Number(v) + Number.EPSILON) * 100) / 100).toFixed(2)
    },
    formatDate(iso) {
      if (!iso) return '—'
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return iso
      const pad = (n) => String(n).padStart(2, '0')
      return (
        pad(d.getDate()) +
        '/' +
        pad(d.getMonth() + 1) +
        '/' +
        d.getFullYear() +
        ' ' +
        pad(d.getHours()) +
        ':' +
        pad(d.getMinutes())
      )
    },
    formatErrors(errors) {
      if (errors == null) return ''
      if (typeof errors === 'string') return errors
      try {
        return JSON.stringify(errors, null, 2)
      } catch (e) {
        return String(errors)
      }
    },
    prettyJson(obj) {
      try {
        return JSON.stringify(obj, null, 2)
      } catch (e) {
        return String(obj)
      }
    },
    normalizePhone(phone) {
      let digits = String(phone || '').replace(/\D+/g, '')
      if (!digits) return ''
      if (digits.length === 9 && digits.startsWith('9')) digits = '51' + digits
      if (digits.length === 11 && digits.startsWith('51')) return digits
      if (digits.length >= 10) return digits
      return digits
    },
    shareMessage() {
      const d = this.detail || {}
      const tipo = d.tipo === 'factura' ? 'Factura' : 'Boleta'
      const label = d.document_label || ('#' + d.invoice_id)
      return (
        'Hola, te compartimos tu ' +
        tipo +
        ' ' +
        label +
        ' por S/ ' +
        this.money(d.total) +
        '.\n\nVer comprobante: ' +
        this.shareUrl
      )
    },
    sendEmail() {
      if (!this.canSendEmail) return
      const d = this.detail || {}
      const tipo = d.tipo === 'factura' ? 'Factura' : 'Boleta'
      const subject = encodeURIComponent(tipo + ' ' + (d.document_label || d.invoice_id))
      const body = encodeURIComponent(this.shareMessage())
      window.open('mailto:' + encodeURIComponent(this.shareEmail) + '?subject=' + subject + '&body=' + body, '_blank')
    },
    sendWhatsapp() {
      if (!this.canSendWhatsapp) return
      const phone = this.normalizePhone(this.sharePhone)
      const text = encodeURIComponent(this.shareMessage())
      window.open('https://wa.me/' + phone + '?text=' + text, '_blank', 'noopener')
    },
    statusLabel(row) {
      if (row.provider === 'nubefact' && row.success) return 'Nubefact'
      if (row.provider === 'nubefact' && !row.success) return 'Error emisión'
      return 'Simulado'
    },
    statusClass(row) {
      if (row.provider === 'nubefact' && row.success) return 'badge--ok'
      if (row.provider === 'nubefact' && !row.success) return 'badge--err'
      return 'badge--mock'
    },
    async openStatus(row) {
      this.statusOpen = true
      this.detail = null
      this.detailError = ''
      this.detailLoading = true
      this.shareEmail = ''
      this.sharePhone = ''
      try {
        const res = await axios.get('general/table/comprobantes/' + row.invoice_id, {
          params: { company_id: this.companyId }
        })
        if (!res.data || !res.data.success) {
          this.detailError = (res.data && res.data.message) || 'No se pudo cargar el estado.'
          return
        }
        this.detail = res.data.data || null
        if (this.detail) {
          this.shareEmail = this.detail.guest_email || ''
          this.sharePhone = this.detail.guest_phone || ''
        }
      } catch (e) {
        this.detailError =
          (e.response && e.response.data && e.response.data.message) || e.message || 'Error de red'
      } finally {
        this.detailLoading = false
      }
    },
    closeStatus() {
      this.statusOpen = false
      this.detail = null
      this.detailError = ''
      this.detailLoading = false
      this.shareEmail = ''
      this.sharePhone = ''
    },
    async load(page) {
      this.page = page || 1
      this.busy = true
      this.error = ''
      try {
        const res = await axios.get('general/table/comprobantes', {
          params: {
            company_id: this.companyId,
            desde: this.desde,
            hasta: this.hasta,
            tipo: this.tipo,
            provider: this.provider,
            q: this.q || undefined,
            page: this.page,
            per_page: 25
          }
        })
        if (!res.data || !res.data.success) {
          this.items = []
          this.summary = null
          this.pagination = null
          this.error = (res.data && res.data.message) || 'No se pudo cargar el listado.'
          return
        }
        const data = res.data.data || {}
        this.summary = data.summary || null
        this.items = data.items || []
        this.pagination = data.pagination || null
      } catch (e) {
        this.items = []
        this.summary = null
        this.pagination = null
        this.error = (e.response && e.response.data && e.response.data.message) || e.message || 'Error de red'
      } finally {
        this.busy = false
      }
    }
  }
}
</script>

<style scoped>
.body { width: 100%; display: flex; }
.body__containt {
  min-height: 87vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 20px 28px 40px;
  box-shadow: 1px 10px 10px rgba(0, 0, 0, 0.1);
  box-sizing: border-box;
  min-width: 0;
}
.page__lead {
  margin: 4px 0 0;
  font-size: 13px;
  color: #777;
}
.page__title {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: 0 0 12px;
}
.page__title h2 {
  margin: 0;
  font-weight: 600;
  color: #ff3128;
  font-size: 25px;
}
.dash-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
  margin: 0 0 14px;
}
.dash-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  color: #666;
}
.dash-field--grow { flex: 1; min-width: 180px; }
.dash-field input,
.dash-field select {
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
  background: #fff;
}
.btn-search {
  border: none;
  border-radius: 9px;
  padding: 10px 16px;
  background: #ff3128;
  color: #fff;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
}
.btn-search:disabled { opacity: 0.55; cursor: not-allowed; }
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}
.kpi-card {
  background: #fff;
  border: 1px solid #eee;
  border-radius: 12px;
  padding: 14px 16px;
}
.kpi-card--accent {
  border-color: #ffd0cd;
  background: #fff7f6;
}
.kpi-card__label {
  margin: 0;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #999;
}
.kpi-card__value {
  margin: 6px 0 2px;
  font-size: 22px;
  color: #222;
}
.kpi-card__meta {
  margin: 0;
  font-size: 12px;
  color: #888;
}
.dash-error {
  color: #b42318;
  background: #fdecec;
  border: 1px solid #f5c2c2;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 13px;
}
.dash-empty {
  color: #888;
  font-size: 13px;
  padding: 28px 8px;
  text-align: center;
}
.table-wrap {
  background: #fff;
  border: 1px solid #eee;
  border-radius: 12px;
  overflow: auto;
}
.cpe-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.cpe-table th {
  text-align: left;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #888;
  padding: 12px 12px;
  border-bottom: 1px solid #eee;
  background: #fafbfc;
  white-space: nowrap;
}
.cpe-table td {
  padding: 12px;
  border-bottom: 1px solid #f3f3f3;
  vertical-align: top;
  color: #333;
}
.cpe-table tr:last-child td { border-bottom: none; }
.cpe-table .num { text-align: right; font-weight: 600; white-space: nowrap; }
.mono { font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 12px; }
.sub { display: block; font-size: 11px; color: #999; margin-top: 2px; }
.badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 999px;
}
.badge--boleta { background: #e8f1ff; color: #2d6cdf; }
.badge--factura { background: #f3e8ff; color: #7a3db8; }
.badge--ok { background: #eaf8f0; color: #1b7a4a; }
.badge--mock { background: #f5f5f5; color: #666; }
.badge--err { background: #fdecec; color: #b42318; }
.actions { display: flex; flex-wrap: wrap; gap: 8px; }
.link {
  color: #2d6cdf;
  font-weight: 600;
  font-size: 12px;
  text-decoration: none;
}
.link:hover { text-decoration: underline; }
.link--btn {
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  font-family: inherit;
}
.pager {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  margin-top: 14px;
  font-size: 13px;
  color: #555;
}
.btn-ghost {
  border: 1px solid #ddd;
  background: #fff;
  border-radius: 8px;
  padding: 7px 12px;
  font-size: 13px;
  cursor: pointer;
  color: #444;
}
.btn-ghost:disabled { opacity: 0.45; cursor: not-allowed; }

.modal {
  position: fixed;
  inset: 0;
  background: rgba(20, 20, 20, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 80;
  padding: 16px;
}
.modal__card {
  width: min(720px, 100%);
  max-height: min(86vh, 900px);
  overflow: auto;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.22);
}
.modal__head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
  padding: 16px 18px;
  border-bottom: 1px solid #eee;
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 1;
}
.modal__eyebrow {
  margin: 0 0 4px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #ff3128;
}
.modal__head h3 {
  margin: 0;
  font-size: 18px;
  color: #1a1a1a;
}
.modal__close {
  border: none;
  background: #f3f3f3;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  color: #555;
}
.modal__body { padding: 16px 18px 20px; }
.status-banner {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  border-radius: 10px;
  margin-bottom: 14px;
  font-size: 13px;
  line-height: 1.4;
}
.status-banner--accepted,
.status-banner--emitted { background: #eaf8f0; border: 1px solid #b7e4c7; color: #1b7a4a; }
.status-banner--error { background: #fdecec; border: 1px solid #f5c2c2; color: #b42318; }
.status-banner--simulate,
.status-banner--skipped { background: #f5f5f5; border: 1px solid #e5e5e5; color: #555; }
.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 14px;
}
.detail-item {
  background: #fafbfc;
  border: 1px solid #eee;
  border-radius: 10px;
  padding: 10px 12px;
}
.detail-item span {
  display: block;
  font-size: 11px;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-bottom: 4px;
}
.detail-item strong {
  font-size: 13px;
  color: #222;
  word-break: break-word;
}
.detail-block {
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 14px;
}
.detail-block--warn {
  background: #fff8e8;
  border: 1px solid #ffe0a3;
  color: #6d4c00;
}
.detail-block h4 {
  margin: 0 0 8px;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.detail-block p { margin: 0 0 6px; font-size: 13px; line-height: 1.4; }
.detail-links {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}
.btn-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  border-radius: 8px;
  background: #2d6cdf;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  text-decoration: none;
  border: none;
  cursor: pointer;
  font-family: inherit;
}
.btn-link:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.btn-link--ghost {
  background: #fff;
  color: #444;
  border: 1px solid #ddd;
}
.btn-link--wa {
  background: #25d366;
}
.share-panel {
  border: 1px solid #eee;
  border-radius: 12px;
  padding: 12px 14px;
  margin-bottom: 14px;
  background: #fafbfc;
}
.share-panel h4 {
  margin: 0 0 10px;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #888;
}
.share-hint {
  margin: 0 0 10px;
  font-size: 12px;
  color: #888;
  line-height: 1.4;
}
.share-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: flex-end;
  margin-bottom: 8px;
}
.share-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 180px;
  font-size: 12px;
  color: #666;
}
.share-field input {
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
  background: #fff;
}
.raw-box {
  border: 1px solid #d8dee4;
  border-radius: 10px;
  padding: 10px 12px;
  background: #f6f8fa;
  color: #1f2328;
}
.raw-box summary {
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 8px;
  color: #24292f;
}
.raw-box pre {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 12px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  line-height: 1.5;
  color: #1f2328 !important;
  background: #ffffff;
  border: 1px solid #eaeef2;
  border-radius: 8px;
  padding: 12px;
}
.detail-block pre {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 12px;
  font-family: ui-monospace, Menlo, Consolas, monospace;
  line-height: 1.45;
  color: #6d4c00;
}
@media (max-width: 960px) {
  .kpi-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .detail-grid { grid-template-columns: 1fr; }
}
</style>
